import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  CommonTableComponent,
  CommonTableColumn,
  CommonTableConfig,
} from '../../components/common-table/common-table.component';
import { UserInfoCellComponent } from '../../components/common-table/user-info-cell/user-info-cell.component';
import { CurrencyCellComponent } from '../../components/common-table/currency-cell/currency-cell.component';
import { StatusCellComponent } from '../../components/common-table/status-cell/status-cell.component';
import { ActionCellComponent } from '../../components/common-table/action-arrow-cell/action-arrow-cell.component';
import { DateRange } from '../../components/date-range-picker/date-range-picker.component';
import { AdminService } from '@smatchy/sdk';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export type OrganizerBalancesSortBy =
  | 'organizerName'
  | 'incomingEarnings'
  | 'availableAmount'
  | 'pendingPayoutAmount'
  | 'stripeConnectedStatus'
  | 'createdAt';

export interface ProOrganizer {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  incomingEarnings: number;
  incomingEventsCount?: number;
  availableAmount: number;
  pendingPayoutAmount: number;
  stripeConnectedStatus: 'connected' | 'not-started' | 'access-revoked';
}

const COLUMN_FIELD_TO_SORT_BY: Record<string, OrganizerBalancesSortBy> = {
  name: 'organizerName',
  incomingEarnings: 'incomingEarnings',
  availableAmount: 'availableAmount',
  pendingPayoutAmount: 'pendingPayoutAmount',
  stripeConnectedStatus: 'stripeConnectedStatus',
};

const SORT_BY_TO_COLUMN_FIELD: Record<OrganizerBalancesSortBy, string> = {
  organizerName: 'name',
  incomingEarnings: 'incomingEarnings',
  availableAmount: 'availableAmount',
  pendingPayoutAmount: 'pendingPayoutAmount',
  stripeConnectedStatus: 'stripeConnectedStatus',
  createdAt: 'createdAt',
};

@Component({
  selector: 'bo-organizer-balances',
  templateUrl: 'organizer-balances.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CommonTableComponent,
    UserInfoCellComponent,
    CurrencyCellComponent,
    StatusCellComponent,
    ActionCellComponent,
  ],
})
export class OrganizerBalancesComponent implements OnInit {
  #adminService = inject(AdminService);
  #router = inject(Router);
  #translate = inject(TranslateService);
  #cdr = inject(ChangeDetectorRef);
  #destroyRef = inject(DestroyRef);
  #searchInput$ = new Subject<string>();

  organizers = signal<ProOrganizer[]>([]);
  loading = signal(false);
  totalCount = signal(0);
  searchValue = signal('');
  dateRange = signal<DateRange>({ startDate: null, endDate: null });
  stripeStatusFilter = signal<'connected' | 'not-started' | 'access-revoked' | null>(null);
  currentPage = signal(1);
  pageSize = signal(10);
  sortBy = signal<OrganizerBalancesSortBy>('createdAt');
  sortOrder = signal<'asc' | 'desc'>('desc');

  columns: CommonTableColumn[] = [];

  tableConfig: CommonTableConfig = {
    table: { showBorder: false },
    toolbar: { globalFilterPlaceholder: '', globalFilterFields: ['name', 'email'], showDateRangePicker: false },
    filters: [],
    pagination: { paginator: true },
  };

  private t(key: string): string {
    return this.#translate.instant(key);
  }

  private applyTranslations(): void {
    this.columns = [
      { label: this.t('financePages.obColProOrganizer'), field: 'name', sortable: true, width: '20%' },
      { label: this.t('financePages.obColIncoming'), field: 'incomingEarnings', sortable: true, width: '10%', align: 'right' },
      { label: this.t('financePages.obColAvailable'), field: 'availableAmount', sortable: true, width: '10%', align: 'right' },
      { label: this.t('financePages.obColPendingPayout'), field: 'pendingPayoutAmount', sortable: true, width: '10%', align: 'right' },
      { label: this.t('financePages.obColStripeStatus'), field: 'stripeConnectedStatus', sortable: true, width: '15%', align: 'right' },
      { label: this.t('financePages.obColActions'), width: '0%', align: 'center' },
    ];
    this.tableConfig = {
      table: { showBorder: false },
      toolbar: {
        globalFilterPlaceholder: this.t('financePages.searchOrganizer'),
        globalFilterFields: ['name', 'email'],
        showDateRangePicker: false,
      },
      filters: [
        {
          field: 'stripeConnectedStatus',
          label: this.t('financePages.obFilterStripeStatus'),
          placeholder: this.t('financePages.obFilterAll'),
          options: [
            { label: this.t('financePages.obFilterAll'), value: null },
            { label: this.t('statusLabels.connected'), value: 'connected' },
            { label: this.t('statusLabels.notStarted'), value: 'not-started' },
            { label: this.t('statusLabels.accessRevoked'), value: 'access-revoked' },
          ],
        },
      ],
      pagination: { paginator: true },
    };
    this.#cdr.markForCheck();
  }

  totalRecords = signal(0);
  sortField = signal<string>('');

  mapFieldToSortBy = (field: string): OrganizerBalancesSortBy | undefined => COLUMN_FIELD_TO_SORT_BY[field];

  ngOnInit(): void {
    this.applyTranslations();
    this.#translate.onLangChange.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.applyTranslations());
    this.#searchInput$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.loadOrganizerBalances());
    this.loadOrganizerBalances();
  }

  loadOrganizerBalances(): void {
    this.loading.set(true);
    const search = this.searchValue().trim() || undefined;
    const stripeStatus = this.stripeStatusFilter() ?? undefined;
    
    this.#adminService
      .getOrganizerBalancesList({
        search,
        stripeStatus,
        page: this.currentPage(),
        limit: this.pageSize(),
        sortBy: this.sortBy(),
        sortOrder: this.sortOrder(),
      })
      .pipe(
        map((res) => {
          if (res?.error || !res?.value) return { data: [], total: 0 };
          return { data: res.value.data ?? [], total: res.value.total ?? 0 };
        }),
      )
      .subscribe({
        next: ({ data, total }) => {
          const list: ProOrganizer[] = (data ?? []).map((item) => ({
            id: item.organizerId,
            name: item.organizer?.displayName ?? '—',
            email: item.organizer?.email ?? '',
            avatarUrl: item.organizer?.avatarUrl,
            incomingEarnings: item.incomingEarnings ?? 0,
            incomingEventsCount: item.incomingEventsCount ?? 0,
            availableAmount: item.availableAmount ?? 0,
            pendingPayoutAmount: item.pendingPayoutAmount ?? 0,
            stripeConnectedStatus: item.stripeConnectedStatus,
          }));
          this.organizers.set(list);
          this.totalCount.set(total);
          this.totalRecords.set(total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  onSearchChange(value: string): void {
    this.searchValue.set(value);
    this.currentPage.set(1);
    this.#searchInput$.next(value);
  }

  onFilterChange(event: { field: string; value: string | number | null | undefined }): void {
    if (event.field === 'stripeConnectedStatus') {
      this.stripeStatusFilter.set(event.value as 'connected' | 'not-started' | 'access-revoked' | null);
      this.currentPage.set(1);
      this.loadOrganizerBalances();
    }
  }

  onClearFilters(): void {
    this.searchValue.set('');
    this.stripeStatusFilter.set(null);
    this.dateRange.set({ startDate: null, endDate: null });
    this.currentPage.set(1);
    this.#searchInput$.next('');
    this.loadOrganizerBalances();
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange.set(range);
  }

  onSortChange(event: { sortBy?: string; sortOrder?: 'asc' | 'desc' }): void {
    this.sortBy.set(event.sortBy as OrganizerBalancesSortBy || 'createdAt');
    this.sortOrder.set(event.sortOrder || 'desc');

    const by = this.sortBy();
    this.sortField.set(SORT_BY_TO_COLUMN_FIELD[by]);
    
    this.currentPage.set(1);
    this.loadOrganizerBalances();
  }

  onPageChange(event: { page: number; limit: number }): void {
    this.currentPage.set(event.page);
    this.pageSize.set(event.limit);
    this.loadOrganizerBalances();
  }

  onRowClick(organizer: ProOrganizer): void {
    this.#router.navigate(['/organizer-balances', organizer.id]);
  }
}
